import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  BookOpen,
  Package,
  LayoutGrid,
  BarChart3,
  Tag,
  Clock,
  KeyRound,
  Store,
  Truck,
  Users,
  Gift,
  Webhook,
  UploadCloud,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import BackToDashboard from "@/components/BackToDashboard";
import { useOptionalMerchant } from "@/lib/useMerchant";

const GUIDES = [
  {
    title: "Dashboard Tabs",
    description: "A field-by-field walkthrough of the Chats, Customers, Orders, Returns, Stuck, and Automations tabs.",
    path: "/tabs-guide",
    icon: LayoutGrid,
    category: "Getting started",
  },
  {
    title: "Status Badges",
    description: "What every status badge means in your Chats, Orders, Returns, and Customer History tabs.",
    path: "/status-guide",
    icon: CheckCircle2,
    category: "Getting started",
  },
  {
    title: "Catalog",
    description: "Product cards, plan limits, stock, sizes, low stock alerts, and how in-DM product search works.",
    path: "/catalog-guide",
    icon: Package,
    category: "Selling",
  },
  {
    title: "Public Storefront",
    description: "What the Public Catalog Page is, what shows per plan, and what triggers it to refresh.",
    path: "/public-catalog-guide",
    icon: Store,
    category: "Selling",
  },
  {
    title: "Shipping Fees & Discounts",
    description: "How flat shipping fees and discount rules work, including priority when more than one offer applies.",
    path: "/discounts-guide",
    icon: Tag,
    category: "Selling",
  },
  {
    title: "Abandoned Cart Reminders",
    description: "How Checkout DM automatically follows up with customers who add to cart but go quiet before checkout.",
    path: "/abandoned-cart-guide",
    icon: Clock,
    category: "Selling",
  },
  {
    title: "Bulk Product Photo Upload",
    description: "How Pro merchants upload up to 50 product photos at once and turn them into draft products.",
    path: "/bulk-image-upload-guide",
    icon: UploadCloud,
    category: "Selling",
  },
  {
    title: "Analytics",
    description: "What every metric on the Checkout DM Analytics page means, including a few narrower than they sound.",
    path: "/analytics-guide",
    icon: BarChart3,
    category: "Growth",
  },
  {
    title: "Referral Program",
    description: "Invite merchants to Checkout DM and earn extra days on your plan for every merchant who subscribes.",
    path: "/referral-guide",
    icon: Gift,
    category: "Growth",
  },
  {
    title: "Team Access",
    description: "How inviting teammates works, what member access looks like, and what team members can and can't do.",
    path: "/team-guide",
    icon: Users,
    category: "Account",
  },
  {
    title: "Webhooks",
    description: "How Checkout DM webhooks work — events, payload fields, signing, verification, and retries.",
    path: "/webhooks-guide",
    icon: Webhook,
    category: "Developers",
  },
  {
    title: "Razorpay API Keys",
    description: "Step-by-step guide to locating your Razorpay Key ID, Key Secret, and Webhook Secret.",
    path: "/razorpay-keys-guide",
    icon: KeyRound,
    category: "Payments",
  },
  {
    title: "Stripe API Keys",
    description: "Step-by-step guide to locating your Stripe Secret Key and Webhook Signing Secret.",
    path: "/stripe-keys-guide",
    icon: KeyRound,
    category: "Payments",
  },
  {
    title: "PayPal API Keys",
    description: "Step-by-step guide to locating your PayPal Client ID, Secret, and Webhook ID.",
    path: "/paypal-keys-guide",
    icon: KeyRound,
    category: "Payments",
  },
  {
    title: "Shiprocket Delivery Tracking",
    description: "Connect Shiprocket's webhook so your Orders tab automatically shows Delivered, RTO, or Failed.",
    path: "/shiprocket-tracking-guide",
    icon: Truck,
    category: "Shipping",
  },
  {
    title: "Shippo Delivery Tracking",
    description: "Connect Shippo's webhook so your Orders tab automatically shows Delivered, Returned, or Failed.",
    path: "/shippo-tracking-guide",
    icon: Truck,
    category: "Shipping",
  },
];

export default function HelpGuide() {
  const { me } = useOptionalMerchant();
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return GUIDES;
    return GUIDES.filter(
      (g) =>
        g.title.toLowerCase().includes(q) ||
        g.description.toLowerCase().includes(q) ||
        g.category.toLowerCase().includes(q)
    );
  }, [query]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const g of filtered) {
      if (!map.has(g.category)) map.set(g.category, []);
      map.get(g.category).push(g);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Help Guide", item: "https://checkoutdm.com/help" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Help Guide | Checkout DM"
        description="Every Checkout DM guide in one place — catalog, payments, shipping, analytics, referrals, webhooks, and more. Search to find what you need."
        path="/help"
        structuredData={[breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-5xl px-6 pt-10 pb-20 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          Everything you need to know.
        </h1>
        <p className="mt-3 max-w-xl text-ink/60">
          Every Checkout DM guide, in one place. Search or browse by topic.
        </p>

        <div className="relative mt-8 max-w-lg">
          <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ink/35" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search guides — e.g. coupons, webhooks, Razorpay"
            data-testid="help-guide-search"
            className="w-full rounded-full border border-ink/15 bg-white py-3 pl-11 pr-4 text-sm font-medium text-ink placeholder:text-ink/35 focus:border-hotpink focus:outline-none"
          />
        </div>

        {filtered.length === 0 ? (
          <p className="mt-10 text-sm text-ink/50">
            No guides match "{query}". Try a different word, or{" "}
            <Link to="/contact" className="font-semibold text-hotpink hover:underline">
              contact support
            </Link>
            .
          </p>
        ) : (
          <div className="mt-10 space-y-10" data-testid="help-guide-results">
            {grouped.map(([category, guides]) => (
              <div key={category}>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/40">{category}</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {guides.map((g) => {
                    const Icon = g.icon;
                    return (
                      <Link
                        key={g.path}
                        to={g.path}
                        data-testid={`help-guide-card-${g.path.replace(/\//g, "")}`}
                        className="group flex items-start gap-3.5 rounded-2xl border border-ink/10 bg-white p-5 transition-colors hover:border-hotpink/40 hover:bg-hotpink/5"
                      >
                        <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-hotpink/10 text-hotpink">
                          <Icon size={17} />
                        </span>
                        <div className="min-w-0">
                          <p className="flex items-center gap-1 font-semibold text-ink">
                            {g.title}
                            <ArrowUpRight
                              size={14}
                              className="flex-none text-ink/30 transition-colors group-hover:text-hotpink"
                            />
                          </p>
                          <p className="mt-1 text-sm leading-relaxed text-ink/60">{g.description}</p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-14 flex items-center gap-3 rounded-2xl border border-ink/10 bg-white p-5">
          <span className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-hotpink/10 text-hotpink">
            <BookOpen size={17} />
          </span>
          <p className="text-sm text-ink/70">
            Can't find what you're looking for?{" "}
            <Link to="/contact" className="font-semibold text-hotpink hover:underline">
              Contact support
            </Link>{" "}
            and we'll help you out directly.
          </p>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
}
